import React, { useState, useEffect } from 'react';
import {
    Box, TextField, Button, CircularProgress, Alert, FormControlLabel, Checkbox, MenuItem, Select, InputLabel, FormControl,
    Typography
} from '@mui/material';
import { motion } from 'framer-motion';
import { BASE_URL } from '../../utils/endpoints';
import { styled } from '@mui/material/styles';
import Editor from 'react-simple-wysiwyg';

const GreenTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: '#2e7d32',
        },
        '&:hover fieldset': {
            borderColor: '#2e7d32',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#2e7d32',
            borderWidth: '2px',
        },
    },
    '& .MuiInputLabel-root': {
        color: '#2e7d32',
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: '#2e7d32',
    },
}));

const GreenSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: '#2e7d32',
        },
        '&:hover fieldset': {
            borderColor: '#2e7d32',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#2e7d32',
            borderWidth: '2px',
        },
    },
    '& .MuiInputLabel-root': {
        color: '#2e7d32',
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: '#2e7d32',
    },
}));

const BrandCreate = ({ onBrandCreated, handleClose }) => {
    const [formData, setFormData] = useState({
        brand_logo: '',
        brand_name: '',
        calc_cashback: '',
        cashback_claim_time: '',
        cashback_rate: '',
        category: '',
        client_link: '',
        client_offer: '',
        deals_description: '',
        default_tracking: false,
        description: '',
        estimated_cashback_date: '',
        hashtags: '',
        meta_description: '',
        meta_tag: '',
        meta_title: '',
        search_keywords: '',
        status: 'active',
        terms_conditions: '',
        tracking_speed: '',
        trending: false,
        user_show_cashback: '',
        visit_link: ''
    });
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch(`${BASE_URL}/categories`);
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const data = await response.json();
                ('Categories API Response:', data);

                const categoriesArray = Array.isArray(data) ? data : data.categories || [];
                setCategories(categoriesArray);
            } catch (err) {
                setError('Error fetching categories: ' + err.message);
                setCategories([]);
            }
        };
        fetchCategories();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        (`Field changed: ${name}, Value: ${value}`);

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleEditorChange = (e) => {
        setFormData(prev => ({
            ...prev,
            description: e.target.value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage?.getItem("authToken");
        if (!token) {
            setError("No authentication token found");
            return;
        }

        setLoading(true);
        setError('');

        try {
            const payload = {
                ...formData,
                calc_cashback: parseFloat(formData.calc_cashback) || 0,
                cashback_rate: parseFloat(formData.cashback_rate) || 0,
                cashback_claim_time: parseInt(formData.cashback_claim_time) || 0,
                tracking_speed: parseInt(formData.tracking_speed) || 0,
                user_show_cashback: formData.user_show_cashback || '0%',
                hashtags: formData.hashtags.split(',').map(item => item.trim()).filter(item => item),
                meta_title: formData.meta_title || 'Socioglamm Brand',
                search_keywords: formData.search_keywords.split(',').map(item => item.trim()).filter(item => item),
                meta_tag: formData.meta_tag.split(',').map(item => item.trim()).filter(item => item),
            };

            const response = await fetch(`${BASE_URL}/brands`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Error: ${response.statusText}`);
            }

            await response.json();
            onBrandCreated();
            handleClose();
        } catch (err) {
            setError('Error creating brand: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const formVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, staggerChildren: 0.1 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <motion.div
            initial="hidden"
            animate="visible"
            variants={formVariants}
        >
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 2,
                    p: 1,
                    borderRadius: 2,
                    alignItems: 'flex-start',
                    height: 'auto',
                    maxHeight: '60vh',
                    overflowY: 'auto',
                    '::-webkit-scrollbar': {
                        display: 'none'
                    },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none'
                }}
            >
                {error && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        style={{ flexBasis: '100%' }}
                    >
                        <Alert severity="error">{error}</Alert>
                    </motion.div>
                )}
                {/* Brand Name - Full Row */}
                <motion.div variants={itemVariants} style={{ flexBasis: '100%' }}>
                    {formData.brand_logo && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                            <img
                                src={formData.brand_logo}
                                alt="Brand Logo Preview"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    borderRadius: '4px',
                                }}
                            />
                        </Box>
                    )}
                    <GreenTextField
                        label="Brand Name"
                        name="brand_name"
                        value={formData.brand_name}
                        onChange={handleChange}
                        required
                        fullWidth
                        size="small"
                    />
                </motion.div>
                <motion.div variants={itemVariants} style={{ flexBasis: '100%' }}>
                    <GreenTextField
                        label="Brand Logo URL"
                        name="brand_logo"
                        value={formData.brand_logo}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        required
                    />
                </motion.div>
                <motion.div variants={itemVariants} style={{ flex: '1 1 250px', minWidth: 220 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="category-select-label">Category</InputLabel>
                        <GreenSelect
                            labelId="category-select-label"
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            label="Category"
                            required
                        >
                            <MenuItem value="" disabled>Select a category</MenuItem>
                            {Array.isArray(categories) && categories.length > 0 ? (
                                categories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.name}>
                                        {cat.name}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem value="" disabled>No categories available</MenuItem>
                            )}
                        </GreenSelect>
                    </FormControl>
                </motion.div>
                <motion.div variants={itemVariants} style={{ flex: '1 1 250px', minWidth: 220 }}>
                    <GreenTextField
                        label="Cashback Rate (%)"
                        name="cashback_rate"
                        type="number"
                        value={formData.cashback_rate}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        InputProps={{ inputProps: { min: 0, max: 100 } }}
                        required
                    />
                </motion.div>
                {/* Client Offer - Full Row */}
                <motion.div variants={itemVariants} style={{ flexBasis: '100%' }}>
                    <GreenTextField
                        label="Client Offer"
                        name="client_offer"
                        value={formData.client_offer}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        required
                    />
                </motion.div>
                {/* Client Link - Full Row */}
                <motion.div variants={itemVariants} style={{ flexBasis: '100%' }}>
                    <GreenTextField
                        label="Client Link"
                        name="client_link"
                        value={formData.client_link}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        required
                    />
                </motion.div>
                {/* Visit Link - Full Row */}
                <motion.div variants={itemVariants} style={{ flexBasis: '100%' }}>
                    <GreenTextField
                        label="Visit Link"
                        name="visit_link"
                        value={formData.visit_link}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        required
                    />
                </motion.div>
                <motion.div variants={itemVariants} style={{ flex: '1 1 250px', minWidth: 220 }}>
                    <GreenTextField
                        label="Estimated Cashback Date"
                        name="estimated_cashback_date"
                        type="date"
                        value={formData.estimated_cashback_date}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        required
                    />
                </motion.div>
                <motion.div variants={itemVariants} style={{ flex: '1 1 250px', minWidth: 220 }}>
                    <GreenTextField
                        label="Hashtags (comma-separated)"
                        name="hashtags"
                        value={formData.hashtags}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        placeholder="e.g. fashion, summer sale, cashback"
                        helperText="Enter multiple hashtags separated by commas"
                        required
                    />
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(formData.hashtags.split(',').map((item) => item.trim()).filter(Boolean)).map((keyword, idx) => (
                            <Box key={idx} sx={{ px: 1, py: 0.2, bgcolor: '#e8f5e9', color: 'green', borderRadius: 1, fontSize: 13 }}>
                                {keyword}
                            </Box>
                        ))}
                    </Box>
                </motion.div>
                <motion.div variants={itemVariants} style={{ flex: '1 1 250px', minWidth: 220 }}>
                    <GreenTextField
                        label="Meta Title"
                        name="meta_title"
                        value={formData.meta_title}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        placeholder="e.g. Brand Name - Best Deals"
                        helperText="Enter meta titles "
                        required
                    />
                </motion.div>
                <motion.div variants={itemVariants} style={{ flex: '1 1 250px', minWidth: 220 }}>
                    <GreenTextField
                        label="Search Keywords (comma-separated)"
                        name="search_keywords"
                        value={formData.search_keywords}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        placeholder="e.g. shoes, bags, accessories"
                        helperText="Enter multiple keywords separated by commas"
                        required
                    />
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(formData.search_keywords.split(',').map((item) => item.trim()).filter(Boolean)).map((keyword, idx) => (
                            <Box key={idx} sx={{ px: 1, py: 0.2, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 1, fontSize: 13 }}>
                                {keyword}
                            </Box>
                        ))}
                    </Box>
                </motion.div>
                <motion.div variants={itemVariants} style={{ flex: '1 1 250px', minWidth: 220 }}>
                    <GreenTextField
                        label="Meta Tag (comma-separated)"
                        name="meta_tag"
                        value={formData.meta_tag}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        placeholder="e.g. trending, new, exclusive"
                        helperText="Enter multiple meta tags separated by commas"
                        required
                    />
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(formData.meta_tag.split(',').map((item) => item.trim()).filter(Boolean)).map((keyword, idx) => (
                            <Box key={idx} sx={{ px: 1, py: 0.2, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 1, fontSize: 13 }}>
                                {keyword}
                            </Box>
                        ))}
                    </Box>
                </motion.div>
                <motion.div variants={itemVariants} style={{ flex: '1 1 250px', minWidth: 220 }}>
                    <GreenTextField
                        label="Cashback Claim Time (days)"
                        name="cashback_claim_time"
                        type="number"
                        value={formData.cashback_claim_time}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        required
                        InputProps={{ inputProps: { min: 0 } }}
                    />
                </motion.div>
                <motion.div variants={itemVariants} style={{ flex: '1 1 250px', minWidth: 220 }}>
                    <GreenTextField
                        label="Tracking Speed (days)"
                        name="tracking_speed"
                        type="number"
                        value={formData.tracking_speed}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        InputProps={{ inputProps: { min: 0 } }}
                        required
                    />
                </motion.div>
                <motion.div variants={itemVariants} style={{ flex: '1 1 250px', minWidth: 220 }}>
                    <GreenTextField
                        label="Calculated Cashback (%)"
                        name="calc_cashback"
                        type="number"
                        value={formData.calc_cashback}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        InputProps={{ inputProps: { min: 0, max: 100 } }}
                        required
                    />
                </motion.div>
                <motion.div variants={itemVariants} style={{ flex: '1 1 250px', minWidth: 220 }}>
                    <GreenTextField
                        label="User Show Cashback (%)"
                        name="user_show_cashback"
                        value={formData.user_show_cashback}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        placeholder="e.g. 5% or Up to 10%"
                        helperText="Enter the cashback format to be displayed to users"
                        required
                    />
                </motion.div>
                <motion.div variants={itemVariants} style={{ display: 'flex', alignItems: 'center', minWidth: 220, gap: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="default_tracking"
                                checked={formData.default_tracking}
                                onChange={handleChange}
                                sx={{
                                    color: '#2e7d32',
                                    '&.Mui-checked': {
                                        color: '#2e7d32',
                                    },
                                }}
                            />
                        }
                        label="Default Tracking"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="trending"
                                checked={formData.trending}
                                onChange={handleChange}
                                sx={{
                                    color: '#2e7d32',
                                    '&.Mui-checked': {
                                        color: '#2e7d32',
                                    },
                                }}
                            />
                        }
                        label="Trending"
                    />
                </motion.div>
                <motion.div variants={itemVariants} style={{ flexBasis: '100%' }}>
                    <GreenTextField
                        label="Meta Description"
                        name="meta_description"
                        value={formData.meta_description}
                        onChange={handleChange}
                        multiline
                        rows={3}
                        fullWidth
                        size="small"
                        required
                    />
                </motion.div>
                <motion.div variants={itemVariants} style={{ flexBasis: '100%' }}>
                    <Typography variant="body1" sx={{ mb: 1, color: '#2e7d32' }}>
                        Description
                    </Typography>
                    <Editor
                        value={formData.description}
                        onChange={handleEditorChange}
                        containerProps={{
                            style: {
                                border: '1px solid #2e7d32',
                                borderRadius: '4px',
                                minHeight: '100px',
                                padding: '8px',
                                backgroundColor: '#fff'
                            }
                        }}
                    />
                </motion.div>
                <motion.div variants={itemVariants} style={{ flexBasis: '100%' }}>
                    <GreenTextField
                        label="Deals Description"
                        name="deals_description"
                        value={formData.deals_description}
                        onChange={handleChange}
                        multiline
                        rows={3}
                        fullWidth
                        required
                        size="small"
                    />
                </motion.div>
                <motion.div variants={itemVariants} style={{ flexBasis: '100%' }}>
                    <GreenTextField
                        label="Terms & Conditions"
                        name="terms_conditions"
                        value={formData.terms_conditions}
                        onChange={handleChange}
                        multiline
                        rows={3}
                        fullWidth
                        size="small"
                        required
                    />
                </motion.div>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexBasis: '100%' }}>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            sx={{ color: '#9ca3af', borderColor: '#9ca3af' }}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button
                            variant="contained"
                            type="submit"
                            sx={{
                                backgroundColor: '#4CAF50',
                                '&:hover': { backgroundColor: '#45a049' },
                            }}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Create Brand'}
                        </Button>
                    </motion.div>
                </Box>
            </Box>
        </motion.div>
    );
};

export default BrandCreate;